import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, CircleHelp } from "lucide-react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Note } from "@/components/automations/primitives"
import { RoutePending } from "@/components/app/states"
import { Metrics } from "@/components/automations/primitives"
import { toMetrics } from "@/components/reports/helpers"
import { DefinitionsCard, ReportFilters, ReportHeader } from "@/components/reports/primitives"
import { automationReportQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

const Search = z.object({ period: z.enum(["7", "30"]).optional(), channel: z.enum(["all", "instagram", "messenger"]).optional() })

export const Route = createFileRoute("/w/$workspace/reports/automations")({ validateSearch: Search, component: AutomationReport })

/** Screen 37. */
function AutomationReport() {
  const { workspace } = useWorkspaceContext()
  const { period = "7", channel = "all" } = Route.useSearch()
  const navigate = Route.useNavigate()
  const report = useQuery({ ...automationReportQuery(workspace.slug, { period, channel }), placeholderData: (p) => p })
  const data = report.data
  const set = (patch: { period?: string; channel?: string }) => void navigate({ search: (prev) => ({ ...prev, ...patch }) as never })
  const params = { workspace: workspace.slug }
  const top = data?.funnel.stages[0]?.value ?? 0
  return (
    <div className="page-wrap auto-page">
      <ReportHeader title="Automation performance" description="Understand what’s working and where your team can help." workspace={workspace.slug} exportPath={`/api/w/${workspace.slug}/reports/automations.csv?period=${period}&channel=${channel}`} exportName="automations">
        <ReportFilters period={period} channel={channel} onPeriod={(v) => set({ period: v })} onChannel={(v) => set({ channel: v })} />
      </ReportHeader>
      {!data ? (
        <RoutePending />
      ) : (
        <>
          <Metrics items={toMetrics(data.metrics)} />
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <CardTitle>From comment to conversation</CardTitle>
                <CardDescription>{data.funnel.automationName ? `${data.funnel.automationName} · distinct contacts at each step` : "No runs in this period"}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-7 pb-3">
                {data.funnel.stages.map((stage, i) => (
                  <div key={stage.label}>
                    <div className="mb-2 flex justify-between gap-3 text-sm">
                      <span>{stage.label}</span>
                      <strong className="font-medium tabular-nums">{stage.value}</strong>
                    </div>
                    <div className="h-5 overflow-hidden rounded-sm bg-muted">
                      <div className="h-full rounded-sm bg-foreground" style={{ width: `${top ? (stage.value / top) * 100 : 0}%`, opacity: 1 - i * 0.16 }} />
                    </div>
                  </div>
                ))}
                {!data.funnel.stages.length && <p className="text-sm text-muted-foreground">The funnel appears once an automation has runs.</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Needs attention</CardTitle>
                <CardDescription>{data.attentionTotal ? `${data.attentionTotal} run${data.attentionTotal === 1 ? "" : "s"} need a decision.` : "Nothing needs a decision."}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {data.attention.map((row) => (
                    <div className="flex justify-between gap-3 py-4 text-sm" key={row.label}>
                      <span>{row.label}</span>
                      <span className="font-medium tabular-nums">{row.value}</span>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="mt-4 w-full" asChild>
                  <Link to="/w/$workspace/automations/runs" params={params} search={{}}>
                    Review run history
                    <ArrowRight />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance by automation</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Automation</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Handed over</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.byAutomation.map((row) => (
                      <TableRow key={row.automationId}>
                        <TableCell className="py-4">
                          <Link className="hover:underline" to="/w/$workspace/automations/runs" params={params} search={{ automationId: row.automationId }}>
                            {row.name}
                          </Link>
                        </TableCell>
                        <TableCell className="py-4 tabular-nums">{row.started}</TableCell>
                        <TableCell className="py-4 tabular-nums">{row.completed}</TableCell>
                        <TableCell className="py-4 tabular-nums">{row.handedOver}</TableCell>
                      </TableRow>
                    ))}
                    {!data.byAutomation.length && (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No runs started in this period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card className="bg-muted/40">
              <CardHeader>
                <CircleHelp className="mb-2 size-5" />
                <CardTitle>Different outcomes, clear definitions.</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">{data.definitions[3]}</p>
              </CardContent>
            </Card>
          </div>
          <div className="mt-6">
            <DefinitionsCard title="Definitions in this report" definitions={data.definitions} />
          </div>
          <Note>
            Completed and handed over are separate outcomes. {data.active} run{data.active === 1 ? " is" : "s are"} waiting or in progress.
          </Note>
        </>
      )}
    </div>
  )
}
